// Week planner domain: event types, colors and pure builders.
//
// The planner shows five event kinds on one week grid (owner decision):
//   task (amber) · meeting (violet) · call (amber family, data source pending)
//   · telegram lead (blue #229ED9 family) · deal deadline (rose outline).
// Visual spec: `design-directions.html`, direction D «Warm Precision»,
// screen «Календарь» — chip fills are the spec tints halved (owner lock:
// «плашки бледнее на 50%»). A deadline is a date, not an event, so it is
// drawn as an outline.

import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/ru.js';

import { formatMoney, formatPersonName } from '../../../shared/format/index.js';
import type { DealListItem, DealStage } from '../../deals/api/deals.client.js';
import type { MeetingItem } from '../../meetings/api/meetings.client.js';
import type { TaskItem } from '../../tasks/api/tasks.client.js';
import { isOverdue } from '../../tasks/lib/taskMeta.js';
import type { TelegramCandidateItem } from '../../telegram-candidates/api/telegram-candidates.client.js';

export type PlannerEventType = 'task' | 'meeting' | 'call' | 'tg' | 'deadline' | 'generic';

/** Types that have a real data source wired today. */
export const WIRED_EVENT_TYPES: readonly PlannerEventType[] = ['task', 'meeting', 'tg', 'deadline'];

/** Per-type chip surface (spec screen «Календарь»): text / fill / border. */
export interface PlannerTypeSurface {
  /** Text tone of the type family — AA on the soft tint. */
  text: string;
  /** Background tint — the spec value halved (owner: «бледнее на 50%»). */
  bg: string;
  /** Hairline border tint from the same family. */
  border: string;
}

/**
 * Chip surface per event type (D «Warm Precision», spec `--ev-*` tokens).
 * Background tints are the spec alphas halved — the owner locked «плашки
 * бледнее на 50%», so light 0.10/0.09 becomes 0.05/0.045 and dark
 * 0.15/0.13 becomes 0.075/0.065. The type stays recognizable through the
 * family text tone and the border tint; deadline keeps a transparent fill.
 */
export const EVENT_TYPE_SURFACE: Record<
  PlannerEventType,
  { light: PlannerTypeSurface; dark: PlannerTypeSurface }
> = {
  task: {
    light: { text: '#92400e', bg: 'rgba(217, 119, 6, 0.05)', border: 'rgba(217, 119, 6, 0.35)' },
    dark: { text: '#fbbf24', bg: 'rgba(245, 158, 11, 0.065)', border: 'rgba(245, 158, 11, 0.40)' },
  },
  meeting: {
    light: { text: '#6d28d9', bg: 'rgba(139, 92, 246, 0.045)', border: 'rgba(139, 92, 246, 0.32)' },
    dark: { text: '#a78bfa', bg: 'rgba(139, 92, 246, 0.075)', border: 'rgba(139, 92, 246, 0.42)' },
  },
  // No workspace-wide call source exists yet; the spec legend treats
  // «Звонок / задача» as one amber family, so call reuses the task surface.
  call: {
    light: { text: '#92400e', bg: 'rgba(217, 119, 6, 0.05)', border: 'rgba(217, 119, 6, 0.35)' },
    dark: { text: '#fbbf24', bg: 'rgba(245, 158, 11, 0.065)', border: 'rgba(245, 158, 11, 0.40)' },
  },
  tg: {
    light: { text: '#0c6a9f', bg: 'rgba(34, 158, 217, 0.05)', border: 'rgba(34, 158, 217, 0.35)' },
    dark: { text: '#62b3e3', bg: 'rgba(34, 158, 217, 0.075)', border: 'rgba(34, 158, 217, 0.45)' },
  },
  // Deadline = outline, not fill — it is a date, not an event.
  deadline: {
    light: { text: '#be123c', bg: 'transparent', border: 'rgba(190, 18, 60, 0.55)' },
    dark: { text: '#fb7185', bg: 'transparent', border: 'rgba(251, 113, 133, 0.55)' },
  },
  // Neutral chip for generic saved-view records whose entity has no planner
  // family of its own (contact/company/document/note/...): warm stone family.
  generic: {
    light: {
      text: '#57534e',
      bg: 'rgba(120, 113, 108, 0.05)',
      border: 'rgba(120, 113, 108, 0.35)',
    },
    dark: {
      text: '#d6d3d1',
      bg: 'rgba(214, 211, 209, 0.065)',
      border: 'rgba(214, 211, 209, 0.40)',
    },
  },
};

/**
 * Vivid type accents — non-text decorations only (list-view status dots).
 * Chips and legends use `EVENT_TYPE_SURFACE`; these saturated family tones
 * keep an 8px dot readable without any AA obligation of its own.
 */
export const EVENT_ACCENT_COLOR: Record<PlannerEventType, { light: string; dark: string }> = {
  task: { light: '#d97706', dark: '#f59e0b' },
  meeting: { light: '#7c3aed', dark: '#a78bfa' },
  call: { light: '#d97706', dark: '#f59e0b' },
  tg: { light: '#229ED9', dark: '#229ED9' },
  deadline: { light: '#e11d48', dark: '#fb7185' },
  generic: { light: '#78716c', dark: '#a8a29e' },
};

/** Max timed cards per week column before «+N more». */
export const WEEK_COLUMN_CARD_CAP = 4;

export interface PlannerEvent {
  /** Prefixed id: `task:<id>`, `meeting:<id>`, `tg:<id>`, `deadline:<dealId>`. */
  id: string;
  eventType: PlannerEventType;
  title: string;
  /** Secondary line: linked deal/contact/company or status label. */
  entityLabel: string | null;
  /**
   * Rich-card line 1: the person the event is about (client/contact name),
   * when the linked entity carries one. Null → the title takes line 1.
   */
  whoLabel: string | null;
  /**
   * Rich-card line 1, right: the money behind the event (deal amount),
   * already formatted. Null → no amount column.
   */
  amountLabel: string | null;
  /** Local day this event belongs to, `YYYY-MM-DD`. */
  dateKey: string;
  /** `HH:mm` label, or null for the all-day zone. */
  timeLabel: string | null;
  /** Minutes since midnight for sorting; null for all-day events. */
  timeMinutes: number | null;
  /** Closed state renders the card struck through. */
  done: boolean;
  overdue: boolean;
  /** Navigation target when the card is clicked. */
  href: string;
  /** Set for tasks — the card checkbox toggles them. */
  taskId: string | null;
}

/** Callback contract shared by the day/week/list planner views. */
export interface PlannerViewHandlers {
  onOpen: (event: PlannerEvent) => void;
  onToggleTask?: ((event: PlannerEvent) => void) | undefined;
}

// ─── Week math (Monday is always the first day — owner decision) ─────────────

export function startOfWeekMonday(date: Dayjs): Dayjs {
  return date.subtract((date.day() + 6) % 7, 'day').startOf('day');
}

export function weekDays(weekStart: Dayjs): Dayjs[] {
  return Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day'));
}

// ─── Deep-link (?week=YYYY-MM-DD mirrors the anchor cursor) ─────────────────

/** URL search param that carries the planner anchor date. */
export const WEEK_URL_PARAM = 'week';

const WEEK_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses the `?week` deep-link value. The param mirrors the anchor cursor
 * date as-is: in week/list view any date inside the week identifies the week
 * (the grid derives its Monday via startOfWeekMonday), in day view it is the
 * exact day. Only a strict `YYYY-MM-DD` calendar date (round-trip checked)
 * is accepted; null tells the caller to fall back to today.
 */
export function parseWeekParam(value: string | null): Dayjs | null {
  if (value === null) return null;
  const trimmed = value.trim();
  if (!WEEK_PARAM_PATTERN.test(trimmed)) return null;
  const parsed = dayjs(trimmed);
  if (!parsed.isValid() || parsed.format('YYYY-MM-DD') !== trimmed) return null;
  return parsed.startOf('day');
}

/** «5–11 октября 2026» / «5–11 October 2026» (per-instance locale). */
export function formatWeekRangeTitle(weekStart: Dayjs, locale: string): string {
  const weekEnd = weekStart.add(6, 'day');
  // One Intl formatter with a single day+month+year pattern feeds every call
  // site, so the deep-link (?week=) and in-app navigation titles cannot
  // diverge. The day token keeps the RU month in CLDR format context — the
  // genitive «октября» (the reference wording); a bare dayjs `MMMM` resolves
  // to the standalone nominative «октябрь», which made the same-month and
  // cross-month branches disagree.
  const fmt = new Intl.DateTimeFormat(locale === 'ru' ? 'ru-RU' : 'en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const startParts = fmt.formatToParts(weekStart.toDate());
  const endParts = fmt.formatToParts(weekEnd.toDate());
  const part = (parts: Intl.DateTimeFormatPart[], type: string): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  const startDay = part(startParts, 'day');
  const startMonth = part(startParts, 'month');
  const endDay = part(endParts, 'day');
  const endMonth = part(endParts, 'month');
  const year = part(endParts, 'year');
  if (weekStart.month() === weekEnd.month()) {
    return `${startDay}–${endDay} ${endMonth} ${year}`;
  }
  return `${startDay} ${startMonth} — ${endDay} ${endMonth} ${year}`;
}

/** «Вторник, 15 сентября» / «Tuesday, September 15». */
export function formatDayTitle(date: Dayjs, locale: string, now: Dayjs): string {
  const sameYear = date.year() === now.year();
  const formatted = date.locale(locale).format(`dddd, D MMMM${sameYear ? '' : ' YYYY'}`);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

/** «вт, 15 сент» / «Tu, Sep 15» — list view group headers. */
export function formatDayShort(date: Dayjs, locale: string): string {
  return date.locale(locale).format('dd, D MMM');
}

// ─── Builders (pure: source item → planner event) ───────────────────────────

function toMinutes(date: Dayjs): number {
  return date.hour() * 60 + date.minute();
}

/** A due date at local midnight means “this day”, not “midnight sharp”. */
function isMidnight(date: Dayjs): boolean {
  return (
    date.hour() === 0 && date.minute() === 0 && date.second() === 0 && date.millisecond() === 0
  );
}

function taskEntityLabel(task: TaskItem): string | null {
  if (task.deal) return task.deal.title;
  if (task.contact) return formatPersonName(task.contact.firstName, task.contact.lastName);
  if (task.company) return task.company.name;
  return null;
}

export function taskToEvent(task: TaskItem): PlannerEvent | null {
  if (!task.dueAt) return null;
  const due = dayjs(task.dueAt);
  const allDay = isMidnight(due);
  const closed = task.status === 'done' || task.status === 'cancelled';
  return {
    id: `task:${task.id}`,
    eventType: 'task',
    title: task.title,
    entityLabel: taskEntityLabel(task),
    // Rich card: line 1 prefers the linked contact's name.
    whoLabel: task.contact ? formatPersonName(task.contact.firstName, task.contact.lastName) : null,
    amountLabel: null,
    dateKey: due.format('YYYY-MM-DD'),
    timeLabel: allDay ? null : due.format('HH:mm'),
    timeMinutes: allDay ? null : toMinutes(due),
    done: closed,
    overdue: isOverdue(task),
    href: `/tasks/${task.id}`,
    taskId: task.id,
  };
}

function meetingEntityLabel(meeting: MeetingItem): string | null {
  if (meeting.contact) {
    return formatPersonName(meeting.contact.firstName, meeting.contact.lastName);
  }
  if (meeting.company) return meeting.company.name;
  if (meeting.deal) return meeting.deal.title;
  if (meeting.location) return meeting.location;
  return null;
}

export function meetingToEvent(meeting: MeetingItem): PlannerEvent {
  const start = dayjs(meeting.startsAt);
  return {
    id: `meeting:${meeting.id}`,
    eventType: 'meeting',
    title: meeting.title,
    entityLabel: meetingEntityLabel(meeting),
    // Rich card: line 1 is the client the meeting is with.
    whoLabel: meeting.contact
      ? formatPersonName(meeting.contact.firstName, meeting.contact.lastName)
      : null,
    amountLabel: null,
    dateKey: start.format('YYYY-MM-DD'),
    timeLabel: start.format('HH:mm'),
    timeMinutes: toMinutes(start),
    // A cancelled meeting stays visible but struck through, as in the old
    // calendar tab.
    done: meeting.status === 'cancelled',
    overdue: false,
    href: `/meetings/${meeting.id}`,
    taskId: null,
  };
}

function excerpt(value: string, max: number): string {
  const singleLine = value.replace(/\s+/g, ' ').trim();
  if (singleLine.length <= max) return singleLine;
  return `${singleLine.slice(0, max - 1).trimEnd()}…`;
}

/**
 * The candidate has no title field; the review proposal carries one depending
 * on the type (task title / interaction summary / note body).
 */
function candidateTitle(item: TelegramCandidateItem): string {
  const proposal = item.proposal ?? {};
  const title = proposal['title'];
  if (typeof title === 'string' && title.trim()) return excerpt(title, 80);
  const summary = proposal['summary'];
  if (typeof summary === 'string' && summary.trim()) return excerpt(summary, 80);
  const body = proposal['body'];
  if (typeof body === 'string' && body.trim()) return excerpt(body, 80);
  if (item.sourceExcerpt) return excerpt(item.sourceExcerpt, 80);
  return item.typeLabel;
}

/**
 * Only the actionable review queue lands on the planner: pending candidates
 * and those waiting for context. Everything else is already processed.
 */
export function tgCandidateToEvent(item: TelegramCandidateItem): PlannerEvent | null {
  if (item.status !== 'pending' && item.status !== 'needs_context') return null;
  const created = dayjs(item.createdAt);
  return {
    id: `tg:${item.id}`,
    eventType: 'tg',
    title: candidateTitle(item),
    entityLabel: item.statusLabel,
    // No person name in the candidate data — the title stays on line 1.
    whoLabel: null,
    amountLabel: null,
    dateKey: created.format('YYYY-MM-DD'),
    timeLabel: created.format('HH:mm'),
    timeMinutes: toMinutes(created),
    done: false,
    overdue: false,
    href: `/telegram-candidates?candidate=${encodeURIComponent(item.id)}`,
    taskId: null,
  };
}

const OPEN_DEAL_STAGES: readonly DealStage[] = ['New', 'Contacted', 'Negotiation'];

/** Deal deadline = `nextActivityDue` of an open deal; rendered all-day. */
export function dealDeadlineToEvent(deal: DealListItem): PlannerEvent | null {
  if (!deal.nextActivityDue) return null;
  if (!OPEN_DEAL_STAGES.includes(deal.stage)) return null;
  const due = dayjs(deal.nextActivityDue);
  return {
    id: `deadline:${deal.id}`,
    eventType: 'deadline',
    title: deal.title,
    entityLabel: formatMoney(deal.amount, deal.currency),
    // Rich card: the client of the deal on line 1, its amount on the right.
    whoLabel: deal.contact ? formatPersonName(deal.contact.firstName, deal.contact.lastName) : null,
    amountLabel: formatMoney(deal.amount, deal.currency),
    dateKey: due.format('YYYY-MM-DD'),
    timeLabel: null,
    timeMinutes: null,
    done: false,
    overdue: due.isBefore(dayjs(), 'minute'),
    href: `/deals/${deal.id}`,
    taskId: null,
  };
}

// ─── Grouping ────────────────────────────────────────────────────────────────

export interface PlannerDayGroup {
  allDay: PlannerEvent[];
  timed: PlannerEvent[];
}

/** Timed events sort by time, then title; all-day keep source order. */
export function groupEventsByDay(events: PlannerEvent[]): Map<string, PlannerDayGroup> {
  const grouped = new Map<string, PlannerDayGroup>();
  for (const event of events) {
    let group = grouped.get(event.dateKey);
    if (!group) {
      group = { allDay: [], timed: [] };
      grouped.set(event.dateKey, group);
    }
    if (event.timeMinutes === null) {
      group.allDay.push(event);
    } else {
      group.timed.push(event);
    }
  }
  for (const group of grouped.values()) {
    group.timed.sort((a, b) => {
      const timeDiff = (a.timeMinutes ?? 0) - (b.timeMinutes ?? 0);
      if (timeDiff !== 0) return timeDiff;
      return a.title.localeCompare(b.title);
    });
  }
  return grouped;
}
